import React from "react";
import { Building2, Shield, Users } from "lucide-react";
import { motion } from "framer-motion";
import { useLanguage } from "../../context/LanguageContext";

const AlYasminahIntro: React.FC<{ content?: any }> = ({ content }) => {
  const { t_api } = useLanguage();
  const title = content?.title || t_api("About Al Yasminah Real Estate", "حول الياسمينة للعقارات");
  const desc = content?.desc || t_api("Al Yasminah Real Estate is a property management and leasing company...", "الياسمينة للعقارات هي شركة إدارة وتأجير العقارات...");
  const image = content?.image;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 font-sans">
      <motion.div
        className="bg-white rounded-lg shadow-lg p-6 md:p-8 border"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        viewport={{ once: false, amount: 0.2 }}
      >
        <div className="flex flex-col md:flex-row items-center md:items-start md:space-x-8 space-y-6 md:space-y-0">
          {image && (
            <motion.div
              className="flex-shrink-0 flex justify-center md:justify-start"
              initial={{ scale: 0.8, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              viewport={{ once: false, amount: 0.2 }}
            >
              <div className="w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 rounded-lg overflow-hidden flex items-center justify-center">
                <img
                  src={image}
                  alt={title}
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          )}

          <div className="flex-1 text-center md:text-left">
            <motion.h2
              className="text-2xl md:text-3xl font-bold text-gray-900 mb-6"
              initial={{ opacity: 0, y: -30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: false, amount: 0.2 }}
            >
              {title}
            </motion.h2>

            <motion.div
              className="space-y-4 text-gray-700 text-sm md:text-base text-justify leading-relaxed mb-6"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              viewport={{ once: false, amount: 0.2 }}
            >
              <div dangerouslySetInnerHTML={{ __html: desc.replace(/\n/g, '<br/>') }} />
            </motion.div>

            <motion.div
              className="mt-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.2 }}
              variants={{
                hidden: {},
                visible: { transition: { staggerChildren: 0.2 } },
              }}
            >
              {[
                { title: t_api("Property Excellence", "التميز العقاري"), desc: t_api("Professionally managed residential apartments and commercial units.", "شقق سكنية ومحلات تجارية تدار باحترافية."), icon: <Building2 className="h-6 w-6" /> },
                { title: t_api("Safety Focused", "التركيز على السلامة"), desc: t_api("Rigorous inspection standards and cleaning protocols for all common areas.", "معايير تفتيش صارمة وبروتوكولات تنظيف للمناطق المشتركة."), icon: <Shield className="h-6 w-6" /> },
                { title: t_api("Tenant Care", "العناية بالمستأجرين"), desc: t_api("Dedicated on-site management team focused on tenant comfort and satisfaction.", "فريق إدارة مخصص في الموقع يركز على راحة المستأجرين."), icon: <Users className="h-6 w-6" /> },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  className="bg-gray-50 p-4 rounded-lg text-center md:text-left"
                  initial={{ opacity: 0, y: 30, scale: 0.9 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  viewport={{ once: false, amount: 0.2 }}
                >
                  <div className="text-red-600 mb-3 flex justify-center md:justify-start">
                    {item.icon}
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-600">{item.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AlYasminahIntro;
